// Normalizes DRF pagination `next` links (which may be absolute) into a relative
// path that can be safely passed to an axios instance configured with
// `baseURL: http://.../api/v1/`.
//
// Examples:
// - https://api.kanalytics.co.bw/api/v1/tasks/?page=2 -> tasks/?page=2
// - http://localhost:8000/api/v1/documents/?page=3 -> documents/?page=3
// - /api/v1/clients/?page=2 -> clients/?page=2
// - clients/?page=2 -> clients/?page=2
export function normalizeApiNextUrl(nextUrl) {
  if (!nextUrl) return nextUrl;

  // Handle absolute URLs.
  if (nextUrl.startsWith('http://') || nextUrl.startsWith('https://')) {
    if (
      process.env.NODE_ENV !== 'production' &&
      nextUrl.startsWith('https://') &&
      (nextUrl.includes('127.0.0.1') || nextUrl.includes('localhost'))
    ) {
      // Helps identify where https next links are coming from in local dev.
      // eslint-disable-next-line no-console
      console.warn('[api] Normalizing absolute https pagination URL:', nextUrl);
    }
    try {
      const parsed = new URL(nextUrl);
      return normalizeApiNextUrl(`${parsed.pathname || ''}${parsed.search || ''}`);
    } catch (_) {
      // Fall through and attempt string-based normalization.
    }
  }

  // Strip the full API prefix so the remainder is relative to the axios baseURL.
  const apiV1Marker = '/api/v1/';
  const apiV1Index = nextUrl.indexOf(apiV1Marker);
  if (apiV1Index !== -1) {
    return nextUrl.slice(apiV1Index + apiV1Marker.length);
  }

  // Fallback: strip just "/api/" if v1 is not in the path (shouldn't happen in
  // normal operation, but keeps older URL formats working).
  const apiMarker = '/api/';
  const apiIndex = nextUrl.indexOf(apiMarker);
  if (apiIndex !== -1) {
    return nextUrl.slice(apiIndex + apiMarker.length);
  }

  // Common variant: "api/v1/..." (without leading slash)
  if (nextUrl.startsWith('api/v1/')) {
    return nextUrl.slice('api/v1/'.length);
  }

  // Older variant: "api/..." (without leading slash or v1)
  if (nextUrl.startsWith('api/')) {
    return nextUrl.slice('api/'.length);
  }

  // If it's just a leading slash (e.g. "/tasks/?page=2"), make it relative.
  if (nextUrl.startsWith('/')) {
    return nextUrl.replace(/^\/+/, '');
  }

  return nextUrl;
}
